// components/AssetManager/GlobalSearchBar.jsx
//
// Classic search UX:
// - Typing updates a local input only (no network calls while typing).
// - Press Enter OR click the search icon to commit the query.
// - Clear button resets both local input and parent query immediately.
// Works with AssetLibrary → AssetBoard (server-side search) and your
// updated backend that returns thumbUrl in fetchAssets.

import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const GlobalSearchBar = ({
  searchQuery,
  setSearchQuery,
  placeholder = 'Search Assets',
  onSearch,
}) => {
  /* Local input state so users can type freely without triggering fetches */
  const [localValue, setLocalValue] = useState(searchQuery || '');

  /* Keep local input in sync if parent resets/changes the query externally */
  useEffect(() => {
    setLocalValue(searchQuery || '');
  }, [searchQuery]);

  const commit = useCallback(
    (value) => {
      const v = value.trim();
      setSearchQuery?.(v);
      if (onSearch) onSearch(v);
    },
    [setSearchQuery, onSearch]
  );

  const handleInputChange = (e) => {
    setLocalValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Prevent form submit / page refresh in some contexts
      e.preventDefault();
      commit(localValue);
    }
  };

  const handleClickSearch = () => {
    commit(localValue);
  };

  const clearSearch = () => {
    setLocalValue('');
    commit(''); // clear immediately
  };

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault(); // safety: pressing Enter commits via keydown
        commit(localValue);
      }}
      sx={{ height: '100%' }}
    >
      <TextField
        fullWidth
        variant="outlined"
        placeholder={placeholder}
        value={localValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        sx={{
          height: '48px', // Matches upload button height
          '.MuiInputBase-root': {
            height: '100%', // Ensures inner input aligns with the parent
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                aria-label="execute search"
                onClick={handleClickSearch}
                edge="start"
                size="small"
              >
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: Boolean(localValue) && (
            <InputAdornment position="end">
              <IconButton aria-label="clear search" onClick={clearSearch}>
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

GlobalSearchBar.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  onSearch: PropTypes.func,
};

export default GlobalSearchBar;
